import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { Pair } from '../../../adl-gen/sys/types';
import { Skill, CompetencyBarsSlideout as SkillGroupBarsSlideout } from '../../../adl-gen/totalinteraction/futurefit/db';
import { withI18n, WithI18nProps } from '../../../internationalization/i18n-hoc';
import { i18next } from '../../../internationalization/i18next';
import { PlotKeyToggleState } from '../../../stores/plot-key-toggle-state';
import { QuizResultsHelper } from '../../../stores/quiz-results-store';
import { compareByGetTwoProperties } from '../../../util/utils';
import { SVGBuilder } from '../svg-builder/svg-builder';
import { BarCollectionProps, getTextWidth, infoIcon, ProgressBarProps, ResolvedGroup, wrapLabel } from './common';
import { DbKey } from '../../../adl-gen/common/db';

const styles = require('./competency-bars.css');

export interface SkillGroupBarsProps extends WithI18nProps {
  skillGroup: ResolvedGroup;
  qr: QuizResultsHelper;
  plotKeyToggleState: PlotKeyToggleState;
}

/** Circular Progress Bar  */
@observer
export class SkillGroupBars0 extends React.Component<SkillGroupBarsProps> {
  private readonly size: number = 500;

  private readonly widthMargin = 10;

  private readonly xStart = this.widthMargin;
  private readonly xEnd = 100 - this.xStart;

  private readonly detailFont = '10px Open SansRegular';

  @observable visibleProgressTooltip: string | undefined;
  @observable visibleDescTooltip: string | undefined;
  @observable visiblePreviousTooltip: string | undefined;
  @observable visibleGroupTooltip: string | undefined;
  @observable visibleIndustryTooltip: string | undefined;

  @action
  hoverOnProgressBar = (id: string) => {
    this.visibleProgressTooltip = id;
  };

  @action
  hoverOnInfo = (id: string) => {
    this.visibleDescTooltip = id;
  };

  @action
  hoverOffProgressBar = () => {
    this.visibleProgressTooltip = undefined;
  };

  @action
  hoverOffInfo = () => {
    this.visibleDescTooltip = undefined;
  };

  @action
  hoverOnPrevious = (id: string) => {
    this.visiblePreviousTooltip = id;
  };

  @action
  hoverOffPrevious = () => {
    this.visiblePreviousTooltip = undefined;
  };

  @action
  hoverOnGroup = (id: string) => {
    this.visibleGroupTooltip = id;
  };

  @action
  hoverOnIndustry = (id: string) => {
    this.visibleIndustryTooltip = id;
  };

  @action
  hoverOffGroup = () => {
    this.visibleGroupTooltip = undefined;
  };

  @action
  hoverOffIndustry = () => {
    this.visibleIndustryTooltip = undefined;
  };

  rawSkillGapRatio = (qr: QuizResultsHelper, skillId: string): number => {
    const actual = this.getScoreFromPlotKey({
      kind: 'skill',
      qr,
      plotkey: 'actual',
      skillId
    });
    const expected = this.getScoreFromPlotKey({
      kind: 'skill',
      qr,
      plotkey: 'expected',
      skillId
    });
    if (expected === 0 || actual === undefined || expected === undefined) {
      return 0;
    } else {
      return actual / expected;
    }
  };

  skillOrder = (qr: QuizResultsHelper, skillId: string): Pair<number, string> => {
    const skillGapRatio = this.rawSkillGapRatio(qr, skillId);
    const skillName = this.props.t(qr.skill(skillId).name);
    return { v1: skillGapRatio, v2: skillName };
  };

  render() {
    const { qr, skillGroup, t } = this.props;

    const groupTitle = skillGroup.heading;

    const skillIds = skillGroup.skillIds
      .filter(skillId => qr.scoreRatioOfExpected({ kind: 'skill', skillId }) !== undefined)
      .sort(compareByGetTwoProperties(text => this.skillOrder(qr, text)));
    const lineOffset = 4;
    const titleOffset = 8.5;
    const yGap = 20;

    const displayDescription = qr.quiz.competencyBars === SkillGroupBarsSlideout.noSkills;

    const descBoxHeight = this.calcDescriptionBoxHeight(
      wrapLabel(groupTitle, this.scale(20), this.detailFont).length
    );

    const dimY = displayDescription
      ? this.scale(lineOffset + titleOffset + yGap + descBoxHeight)
      : // Assume displaying skills
      this.scale(lineOffset + titleOffset + yGap + yGap * skillIds.length);

    const builder = new SVGBuilder(1, {
      posX: 0,
      posY: 0,
      dimX: this.size,
      dimY
    });

    const { ranges } = qr;

    const factor = (100 - 2 * this.widthMargin) / 100;
    const extents: number[] = ranges.map(r => (r.scoreRange.range.v2 - r.scoreRange.range.v1 + 1) * factor);
    // Remove the last extent
    extents.pop();

    // Range Markers.
    // Initialize
    let xOffset = this.xStart;

    const separators = [
      this.xStart,
      ...extents.map(extent => {
        const separator = xOffset + extent;
        xOffset = separator;
        return separator;
      }),
      this.xEnd
    ].map(this.scale);

    let yOffset = lineOffset;

    for (const x of separators) {
      builder.addLine({
        x1: x,
        y1: this.scale(yOffset - 2),
        x2: x,
        y2: this.scale(yOffset),
        style: 'solid',
        stroke: 'light'
      });
      if (x !== this.scale(this.xStart) && x !== this.scale(this.xEnd)) {
        builder.addLine({
          x1: x,
          y1: this.scale(yOffset),
          x2: x,
          y2: dimY,
          style: 'dashed',
          stroke: 'faint'
        });
      }
    }
    builder.addLine({
      x1: this.scale(this.xStart),
      y1: this.scale(yOffset),
      x2: this.scale(this.xEnd),
      y2: this.scale(yOffset),
      style: 'solid',
      stroke: 'light'
    });

    // Range Markers.
    // Re-initialize
    xOffset = this.xStart;

    ranges.map(r => {
      builder.addText({
        x: this.scale(xOffset + ((r.scoreRange.range.v2 - r.scoreRange.range.v1 + 1) * factor) / 2),
        y: this.scale(yOffset - 2),
        text: t(r.scoreRange.label),
        anchor: 'center',
        style: 'detail'
      });
      xOffset = xOffset + (r.scoreRange.range.v2 - r.scoreRange.range.v1 + 1) * factor;
    });

    yOffset += titleOffset;

    {

      const iconWidth = 0;
      const margin = 0;
      const iconYOffset = 0;
      const barHeight = 3;
      const tooltipYOffset = -1.2;

      this.renderBarCollection(builder, {
        qr,
        title: groupTitle,
        desc: groupTitle,
        iconWidth,
        barHeight,
        margin,
        iconYOffset,
        tooltipYOffset,
        style: 'title',
        yOffset,
        // We don't have a "competency" ID - use group title
        id: groupTitle
      });
    }

    /// skill bars - ignore competencyGroupBarsSlideout settings
    for (const skillId of skillIds) {
      yOffset += yGap;
      const title = t(qr.skill(skillId).name);
      const desc = t(qr.skill(skillId).definition);

      const iconWidth = 2.5;
      const barHeight = 2;
      const margin = 1.5;
      const iconYOffset = -0.2;
      const tooltipYOffset = -0.7;

      this.renderBarCollection(builder, {
        qr,
        title,
        desc,
        iconWidth,
        barHeight,
        margin,
        iconYOffset,
        tooltipYOffset,
        style: 'subtitle',
        yOffset,
        id: skillId
      });
    }

    return <div className={styles.container}>{builder.build()}</div>;
  }

  renderBarCollection(
    builder: SVGBuilder,
    {
      qr,
      yOffset,
      id,     // Group title or skillId
      title,  // Group title or skill name
      desc,   // Group title or skill desc
      iconWidth,
      barHeight,
      margin,
      iconYOffset,
      tooltipYOffset,
      style
    }: BarCollectionProps
  ) {
    const toolTipLines = wrapLabel(desc, this.scale(20), this.detailFont);
    /// Desc not displayed unless no skills
    const descBoxLines = wrapLabel(desc, this.scale(20), this.detailFont);

    /// SS-514 remove info icon from Competency bars, and set title x-coord back to xStart.
    /// Detect this by checking margin, iconWidth and iconYOffset.
    /// If all three are zero, no icon.
    if (iconWidth !== 0 && iconYOffset !== 0 && margin !== 0) {
      this.addTooltip(
        builder,
        toolTipLines,
        this.xStart + iconWidth / 2,
        yOffset + tooltipYOffset,
        this.visibleDescTooltip === id
      );
      this.addInfoIcon(builder, id, this.xStart + iconWidth / 2, yOffset + iconYOffset, iconWidth);
    }

    builder.addText({
      x: this.scale(this.xStart + iconWidth + margin),
      y: this.scale(yOffset),
      text: title,
      anchor: 'right',
      style
    });

    /// Grab this for the descriptionbox if required
    const lastYOffset = this.addProgressBar(
      builder,
      yOffset,
      qr,
      barHeight,
      // We have no competency ID
      id !== this.props.skillGroup.heading ? id : undefined
    );

    /// If any of these three are non-zero, assume it's a competency bar (see above).
    /// Add the description box, if we're not displaying skills.
    if (iconWidth === 0 || iconYOffset === 0 || margin === 0) {
      this.addDescriptionBox(
        builder,
        descBoxLines,
        this.xStart + iconWidth / 2,
        lastYOffset,
        qr.quiz.competencyBars === SkillGroupBarsSlideout.noSkills
      );
    }
  }

  getWidthFromPlotKey(props: ProgressBarProps): number | undefined {
    const score = this.getScoreFromPlotKey(props);
    if (score === undefined) {
      return undefined;
    } else {
      return this.getWidthFromScore(score);
    }
  }

  // We can't use a dataset for the 'competency' scores here, because they don't exist
  // (or there's only one and it's the same for all skill groups on the same competency).
  // However, we *can* use this for skill and group. Set 'competency' = avg of skills in group
  // here in the constructor.
  // keyof Plot in ( actual,expected,previous,difference,industry,group)
  // PlotType in (kind:'overall', kind: 'competency' (+comp ID), 'skill' (+skillId))

  getScoreFromPlotKey(props: ProgressBarProps): number | undefined {
    const skillIds = this.props.skillGroup.skillIds;
    if (!props.qr.plotExists(props.plotkey)) {
      return undefined;
    }
    if (props.kind === 'competency') {
      // Pretend the group is the 'competency' and calc numbers here
      switch (props.plotkey) {
        case 'actual':
        case 'expected':
        case 'industry':
          return Math.round(skillIds
            .map(skillId => props.qr.score(props.plotkey, {
              kind: 'skill',
              skillId
            }))
            .reduce((acc, next) => {
              acc += next;
              return acc;
            }, 0)
            / skillIds.length);
        case 'group':
          // cop-out but not really important
          return props.qr.score(props.plotkey, {
            kind: 'overall'
          });
        default: return undefined
      }
    } else {
      return props.qr.score(props.plotkey, {
        kind: 'skill',
        skillId: props.skillId
      });
    }
  }

  scoreRatioOfExpectedText(qr: QuizResultsHelper, skillId?: DbKey<Skill>): string {
    const skillIds = this.props.skillGroup.skillIds;
    if (skillId === undefined) {
      const actual = Math.round(skillIds
        .map(skillId => qr.score('actual', {
          kind: 'skill',
          skillId
        }))
        .reduce((acc, next) => {
          acc += next;
          return acc;
        }, 0)
        / skillIds.length);
      const expected = Math.round(skillIds
        .map(skillId => qr.score('expected', {
          kind: 'skill',
          skillId
        }))
        .reduce((acc, next) => {
          acc += next;
          return acc;
        }, 0)
        / skillIds.length);
      if (expected === 0) {
        return `[actual ${Math.round(actual)}]`;
      } else if (qr.isDebuggingReports()) {
        return `${Math.round(actual)}/${Math.round(expected)}`;
      } else {
        return `${Math.round((actual / expected) * 100)} %`;
      }
    } else {
      return qr.scoreRatioOfExpectedText({
        kind: 'skill',
        skillId
      });
    }
  }

  getWidthFromScore(score: number): number {
    return (score * (this.xEnd - this.xStart)) / 100;
  }

  /// When not displaying skills, only competency, drop a description box
  /// in the empty space under the competency bars
  addDescriptionBox(builder: SVGBuilder, lines: string[], x: number, y: number, isVisible: boolean) {
    // Get whole width
    const boxWidth = this.getWidthFromScore(100);
    // (Math.max(...lines.map(l => getTextWidth(l, "10px Open SansRegular"))) /
    //   this.size) *
    //   100 +
    // 5;
    const boxHeight = this.calcDescriptionBoxHeight(lines.length);
    builder.addTextBox({
      x: this.scale(x),
      y: this.scale(y),
      width: this.scale(boxWidth),
      height: this.scale(boxHeight),
      text: lines.join(' '),
      style: 'detail',
      rectStyle: 'white',
      isVisible
    });
  }

  calcDescriptionBoxHeight(numLines: number): number {
    const lineHeight = 2.5;
    const boxHeight = lineHeight * (numLines + 1);
    const offset = 0;
    return boxHeight - offset;
  }

  addTooltip(builder: SVGBuilder, lines: string[], x: number, y: number, isVisible: boolean) {
    const boxWidth = (Math.max(...lines.map(l => getTextWidth(l, '8px Open SansRegular'))) / this.size) * 100 + 5;
    const lineHeight = 2.5;
    const boxHeight = lineHeight * lines.length + 2.5;
    const offset = 0.5;
    builder.addRect({
      x: this.scale(x - boxWidth / 2),
      y: this.scale(y - boxHeight - offset),
      width: this.scale(boxWidth),
      height: this.scale(boxHeight - offset),
      style: 'white',
      isVisible
    });
    let i = lines.length;
    for (const line of lines) {
      builder.addText({
        x: this.scale(x),
        y: this.scale(y - offset - i * lineHeight),
        style: 'detail',
        anchor: 'center',
        text: line,
        isVisible
      });
      i--;
    }
  }

  addProgressBar(
    builder: SVGBuilder,
    yOffset: number,
    qr: QuizResultsHelper,
    rectHeight: number,
    skillId?: string
  ): number {
    const { skillGroup, plotKeyToggleState, t } = this.props;
    let localYOffset = yOffset;
    const backgroundWidth = this.getWidthFromScore(100);
    const expectedWidth = this.getWidthFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'expected' }
        : { kind: 'skill', qr, plotkey: 'expected', skillId }
    );
    const actualWidth = this.getWidthFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'actual' }
        : { kind: 'skill', qr, plotkey: 'actual', skillId }
    );
    const previousWidth = this.getWidthFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'previous' }
        : { kind: 'skill', qr, plotkey: 'previous', skillId }
    );
    const groupWidth = this.getWidthFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'group' }
        : { kind: 'skill', qr, plotkey: 'group', skillId }
    );
    const industryWidth = this.getWidthFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'industry' }
        : { kind: 'skill', qr, plotkey: 'industry', skillId }
    );

    /// DEBUG values
    const previousScore = this.getScoreFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'previous' }
        : { kind: 'skill', qr, plotkey: 'previous', skillId }
    );
    const groupScore = this.getScoreFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'group' }
        : { kind: 'skill', qr, plotkey: 'group', skillId }
    );
    const industryScore = this.getScoreFromPlotKey(
      skillId === undefined
        ? { kind: 'competency', qr, plotkey: 'industry' }
        : { kind: 'skill', qr, plotkey: 'industry', skillId }
    );

    const barOffset = 5;
    localYOffset += barOffset;

    builder.addRect({
      x: this.scale(this.xStart),
      y: this.scale(localYOffset),
      style: 'white',
      width: this.scale(backgroundWidth),
      height: this.scale(rectHeight),
      onMouseEnter: skillId
        ? () => this.hoverOnProgressBar(skillId)
        : () => this.hoverOnProgressBar(skillGroup.heading),
      onMouseLeave: this.hoverOffProgressBar
    });

    if (expectedWidth !== undefined && plotKeyToggleState.isShown('expected')) {
      const desiredMarkerY1 = localYOffset - 1.5;
      builder.addRect({
        x: this.scale(this.xStart),
        y: this.scale(localYOffset),
        style: 'grey',
        width: this.scale(expectedWidth),
        height: this.scale(rectHeight),
        onMouseEnter: skillId
          ? () => this.hoverOnProgressBar(skillId)
          : () => this.hoverOnProgressBar(skillGroup.heading),
        onMouseLeave: this.hoverOffProgressBar
      });
      builder.addLine({
        x1: this.scale(expectedWidth + this.xStart),
        y1: this.scale(desiredMarkerY1),
        x2: this.scale(expectedWidth + this.xStart),
        y2: this.scale(localYOffset + rectHeight),
        style: 'solid'
      });
      builder.addCircle({
        x: this.scale(expectedWidth + this.xStart),
        y: this.scale(desiredMarkerY1),
        r: this.scale(0.7),
        style: 'filled'
      });
      builder.addText({
        x: this.scale(expectedWidth + this.xStart - 2),
        y: this.scale(desiredMarkerY1) + 1,
        /// --- [ Possible TODO change if legend is quiz-specific ] ---
        text: i18next.t('competency-bars.legend.desired', 'DESIRED'),
        style: 'marker',
        anchor: 'left'
      });
    }

    if (actualWidth !== undefined && plotKeyToggleState.isShown('actual')) {
      const scoreText = this.scoreRatioOfExpectedText(qr, skillId);

      const lines = [
        ...i18next.t('competency-bars.tooltips.towards-desired', { returnObjects: true, percent: scoreText })
      ];
      const progressBarCount = builder.incrementRectCount();
      builder.addRect({
        x: this.scale(this.xStart),
        y: this.scale(localYOffset),
        style: 'orange',
        width: this.scale(actualWidth),
        height: this.scale(rectHeight),
        onMouseEnter: skillId
          ? () => this.hoverOnProgressBar(skillId)
          : () => this.hoverOnProgressBar(skillGroup.heading),
        onMouseLeave: this.hoverOffProgressBar,
        id: progressBarCount
      });
      this.addTooltip(
        builder,
        lines,
        this.xStart + actualWidth,
        localYOffset,
        skillId ? skillId === this.visibleProgressTooltip : skillGroup.heading === this.visibleProgressTooltip
      );
    }

    const plotOffset = 2;
    localYOffset += rectHeight + plotOffset;
    if (previousWidth !== undefined && plotKeyToggleState.isShown('previous')) {
      this.renderPreviousBar(
        builder,
        previousWidth,
        localYOffset,
        plotOffset,
        /// FF2-426
        qr.isDebuggingReports() && previousScore
          ? `${Math.round(previousScore)}`
          : i18next.t('competency-bars.tooltips.previous', 'Previous Results'),
        skillId
      );
    }


    localYOffset += plotOffset;
    if (groupWidth !== undefined && plotKeyToggleState.isShown('group')) {
      const groupLabel = qr.getGroupLabel(t);
      this.renderGroupBar(
        builder,
        groupWidth,
        localYOffset,
        plotOffset,
        /// FF2-426
        qr.isDebuggingReports() && groupScore
          ? `${Math.round(groupScore)}`
          : groupLabel !== undefined
            ? groupLabel
            : i18next.t('competency-bars.tooltips.group', 'Group Average'),
        skillId
      );
    }

    localYOffset += plotOffset;
    if (industryWidth !== undefined && plotKeyToggleState.isShown('industry')) {
      const industryLabel = qr.getIndustryLabel(t);
      this.renderIndustryBar(
        builder,
        industryWidth,
        localYOffset,
        plotOffset,
        /// FF2-426
        qr.isDebuggingReports() && industryScore
          ? `${Math.round(industryScore)}`
          : industryLabel !== undefined
            ? industryLabel
            : i18next.t('competency-bars.tooltips.industry', 'Industry Average'),
        skillId
      );
    }

    /// Return the current yOffset plus the right amount of space to add
    return localYOffset + plotOffset;
  }

  addInfoIcon(builder: SVGBuilder, id: string, x: number, y: number, size: number) {
    const iconCount = builder.incrementCustomItemCount();
    builder.addCustomElement(
      infoIcon({
        key: id,
        x: this.scale(x - size / 2),
        y: this.scale(y - size / 2),
        width: this.scale(size),
        height: this.scale(size),
        onMouseEnter: () => this.hoverOnInfo(id),
        onMouseLeave: () => this.hoverOffInfo(),
        iconCount
      })
    );
    return builder;
  }

  renderPreviousBar(
    builder: SVGBuilder,
    width: number,
    yOffset: number,
    plotOffset: number,
    tooltip: string,
    skillId?: string
  ) {
    builder.addLine({
      x1: this.scale(this.xStart),
      y1: this.scale(yOffset),
      x2: this.scale(width + this.xStart),
      y2: this.scale(yOffset),
      style: 'dashed',
      stroke: 'dark'
    });
    builder.addRect({
      x: this.scale(this.xStart),
      y: this.scale(yOffset - plotOffset / 2),
      width: this.scale(width + 0.7),
      height: this.scale(plotOffset),
      style: 'transparent',
      onMouseEnter: skillId ? () => this.hoverOnPrevious(skillId) : () => this.hoverOnPrevious(this.props.skillGroup.heading),
      onMouseLeave: this.hoverOffPrevious
    });
    this.addTooltip(
      builder,
      [tooltip],
      this.xStart + width,
      yOffset - plotOffset / 2,
      skillId ? skillId === this.visiblePreviousTooltip : this.props.skillGroup.heading === this.visiblePreviousTooltip
    );
  }

  renderGroupBar(
    builder: SVGBuilder,
    width: number,
    yOffset: number,
    plotOffset: number,
    tooltip: string,
    skillId?: string
  ) {
    builder.addLine({
      x1: this.scale(this.xStart),
      y1: this.scale(yOffset),
      x2: this.scale(width + this.xStart),
      y2: this.scale(yOffset),
      style: 'dotted'
    });
    builder.addCircle({
      x: this.scale(width + this.xStart),
      y: this.scale(yOffset),
      style: 'shaded',
      r: this.scale(0.7)
    });
    builder.addRect({
      x: this.scale(this.xStart),
      y: this.scale(yOffset - plotOffset / 2),
      width: this.scale(width + 0.7),
      height: this.scale(plotOffset),
      style: 'transparent',
      onMouseEnter: skillId ? () => this.hoverOnGroup(skillId) : () => this.hoverOnGroup(this.props.skillGroup.heading),
      onMouseLeave: this.hoverOffGroup
    });
    this.addTooltip(
      builder,
      [tooltip],
      this.xStart + width,
      yOffset - plotOffset / 2,
      skillId ? skillId === this.visibleGroupTooltip : this.props.skillGroup.heading === this.visibleGroupTooltip
    );
  }

  renderIndustryBar(
    builder: SVGBuilder,
    width: number,
    yOffset: number,
    plotOffset: number,
    tooltip: string,
    skillId?: string
  ) {
    builder.addLine({
      x1: this.scale(this.xStart),
      y1: this.scale(yOffset),
      x2: this.scale(width + this.xStart),
      y2: this.scale(yOffset),
      style: 'dashed'
    });
    builder.addTriangle({
      x: this.scale(width + this.xStart),
      y: this.scale(yOffset),
      size: this.scale(0.7)
    });
    builder.addRect({
      x: this.scale(this.xStart),
      y: this.scale(yOffset - plotOffset / 2),
      width: this.scale(width + 0.7),
      height: this.scale(plotOffset),
      style: 'transparent',
      onMouseEnter: skillId ? () => this.hoverOnIndustry(skillId) : () => this.hoverOnIndustry(this.props.skillGroup.heading),
      onMouseLeave: this.hoverOffIndustry
    });
    this.addTooltip(
      builder,
      [tooltip],
      this.xStart + width,
      yOffset - plotOffset / 2,
      skillId ? skillId === this.visibleIndustryTooltip : this.props.skillGroup.heading === this.visibleIndustryTooltip
    );
  }

  scale = (num: number): number => {
    return (num * this.size) / 100;
  };
}

export const SkillGroupBars = withI18n(SkillGroupBars0);




// WEBPACK FOOTER //
// ./src/ui/components/competency-bars/group-bars.tsx